import React, { Component, FC } from 'react';
import { injectIntl, useIntl, WrappedComponentProps } from 'react-intl';
import { connect } from 'react-redux';

import * as IBooks from '../../../../../interfaces/merchants/resources/books';
import * as IInputs from '../../../../../interfaces/merchants/resources/books/inputs';
import {
  inputBookingTargets,
  RestartReservationError,
} from '../../../../../redux/actions/merchants/resources/books/inputs';
import * as B from '../../../../../redux/reducers/merchants/resources/books';
import * as I from '../../../../../redux/reducers/merchants/resources/books/inputs';

import * as $ from '../../../../../helpers/merchants/resources/helper';

import LabeledContainer from '../../../../../molecules/common/LabeledContainer';
import BooksSelectedMenu from '../../../../../molecules/merchant/resources/book/BooksSelectedMenu';
import BooksSelectedStaff from '../../../../../molecules/merchant/resources/book/BooksSelectedStaff';

import InputBookingTargetCourseSelector from '../InputBookingTargetCourseSelector';
import InputBookingTargetOptionSelector from '../InputBookingTargetOptionSelector';
import InputBookingTargetStaffSelector from '../InputBookingTargetStaffSelector';
import InputBookingTargetSwitch from '../../../../../molecules/merchant/resources/book/InputBookingTargetSwitch';

import css from './InputBookingTargetWithStaff.module.scss';
import { scrollToTop } from '../../../../../hooks/useScrollToTop';
import { RSButton } from 'src/reservation-page/RSButton';

interface ElementProps {
  modelType: IBooks.ModelType;
  settings: IBooks.SettingBookingTargets;
  postable: boolean;
  onSubmit: () => void;
  onRestart: () => void;
  renew?: boolean;
  withExpressFlow: boolean;
  resourcePublicId: string;
}

interface StateProps {
  inputs: IInputs.State;
}

interface DispatchProps {
  actions: {
    handleSubmit: (
      modelType: IBooks.ModelType,
      merchantId: string,
      resourceId: string,
      course: IBooks.Course,
      options: IBooks.Option[],
      staff: IBooks.Staff,
      selected: IBooks.Switch,
      withExpressFlow: boolean,
      reservationId?: number,
    ) => Promise<void>;
  };
}

interface State {
  selected: IBooks.Switch;
  course?: IBooks.Course;
  options?: IBooks.Option[]; // undefined: 未選択、[]: 0個選択中
  staff?: IBooks.Staff;
  selecting: {
    options?: IBooks.Option[]; // undefined: 未選択、[]: 0個選択中
  };
}

type Props = ElementProps & StateProps & DispatchProps & WrappedComponentProps;

interface StaffProps {
  staff: IBooks.Staff;
  onChangeTarget: (course: boolean, options: boolean, staff?: boolean) => void;
  args: [boolean, boolean, boolean?];
  withBorderBottom?: boolean;
  withBorderTop?: boolean;
  withPaddingLarge?: boolean;
}
const Staff: FC<StaffProps> = ({
  staff,
  onChangeTarget,
  args,
  withPaddingLarge,
  withBorderBottom,
}) => {
  const intl = useIntl();
  return (
    <BooksSelectedStaff
      name={
        $.isRandomStaff(staff)
          ? intl.formatMessage({ id: 'books.booksMenu.noSelect' })
          : staff.name
      }
      id={staff.id}
      changeStaff={() => onChangeTarget.apply(null, args)}
      withPaddingLarge={withPaddingLarge}
      withBorderBottom={withBorderBottom}
    />
  );
};

interface CurrentCourseOptionsProps {
  course?: IBooks.Course;
  options?: IBooks.Option[];
  onChangeCourse: () => void;
  onChangeOptions: () => void;
}

const CurrentCourseOptions: FC<CurrentCourseOptionsProps> = ({
  course,
  options,

  onChangeCourse,
  onChangeOptions,
}) => {
  const intl = useIntl();
  if (!course && options && options.length === 0) {
    return null;
  }
  return (
    <div>
      {course && (
        <div>
          <LabeledContainer
            label={intl.formatMessage({
              id: 'books.formsLabel.selectedBooks',
            })}
            contentsPadding={true}
          >
            <BooksSelectedMenu
              name={course.name}
              options={options || []}
              price={course.price}
              duration={course.duration}
              changeTarget={onChangeCourse}
              changeOption={onChangeOptions}
            />
          </LabeledContainer>
        </div>
      )}
    </div>
  );
};

interface CurrentStaffCourseProps {
  staff?: IBooks.RealStaff;
  course?: IBooks.Course;
  onChangeStaff: () => void;
  onChangeCourse: () => void;
}

const CurrentStaffCourse: FC<CurrentStaffCourseProps> = ({
  staff,
  course,

  onChangeStaff,
  onChangeCourse,
}) => {
  const intl = useIntl();
  if (!staff && !course) {
    return null;
  }
  return (
    <div className={css.CurrentStaffCourse}>
      {staff && (
        <LabeledContainer
          label={intl.formatMessage({
            id: 'books.formsLabel.selectedBooks',
          })}
          contentsPadding={true}
        >
          <Staff
            staff={staff!}
            onChangeTarget={onChangeStaff}
            args={[false, false, true]}
            withPaddingLarge={true}
          />
          {course && (
            <div className={css.CurrentStaffCourse__Menu}>
              <BooksSelectedMenu
                name={course.name}
                options={[]}
                price={course.price}
                duration={course.duration}
                changeTarget={onChangeCourse}
              />
            </div>
          )}
        </LabeledContainer>
      )}
    </div>
  );
};

const showableSwitch = (
  selected: IBooks.Switch,
  course?: IBooks.Course,
  staff?: IBooks.Staff,
): boolean => {
  if (selected === IBooks.Switch.Course && !!course) {
    return false;
  }

  if (selected === IBooks.Switch.Staff && !!staff) {
    return false;
  }
  return true;
};

const selectedBy = (
  settings: IBooks.SettingBookingTargets,
  renew?: boolean,
) => {
  if (renew) {
    return IBooks.Switch.Course;
  }
  if (settings.selected) {
    return settings.selected;
  }
  // コースとスタッフ両選択時画面もStaffで表示されるのでこの分岐に入る
  if (settings.selectedStaff) {
    return IBooks.Switch.Staff;
  }
  if (settings.selectedCourse) {
    return IBooks.Switch.Course;
  }
  return IBooks.Switch.Course;
};

class InputBookingTarget extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const {
      inputs: { course, options, staff },
      settings,
      renew,
    } = props;
    this.state = {
      course: renew ? undefined : course,
      options: renew ? undefined : options,
      selected: selectedBy(settings, renew),
      selecting: { options: renew ? undefined : options },
      staff: renew ? undefined : staff,
    };
    this.handleClickSwitch = this.handleClickSwitch.bind(this);

    this.handleClickCourse = this.handleClickCourse.bind(this);
    this.handleClickOption = this.handleClickOption.bind(this);
    this.handleClickStaff = this.handleClickStaff.bind(this);

    this.handleClearTarget = this.handleClearTarget.bind(this);
    this.handleFixSelection = this.handleFixSelection.bind(this);

    this.handleSubmit = this.handleSubmit.bind(this);
  }

  public componentDidMount() {
    const {
      inputs: { reservationId },
      settings: { selectedCourse, selectedStaff },
    } = this.props;
    scrollToTop();
    if (reservationId) return;

    const newState = { ...this.state };

    if (selectedCourse) {
      newState.course = selectedCourse;
    }
    if (selectedStaff) {
      newState.staff = selectedStaff;
    }

    this.setState(newState);
  }

  // ブラウザバックからフローを再開した場合、componentDidMount時点のpropsは以前の状態となっている
  // componentDidUpdateで、改めて初期化された後のpropsをstateに反映する
  public componentDidUpdate(prevProps: Props) {
    const prevSettings = prevProps.settings;
    const {
      selectedCourse: prevSelectedCourse,
      selectedStaff: prevSelectedStaff,
    } = prevSettings;

    const settings = this.props.settings;
    const { selectedCourse, selectedStaff } = settings;

    if (
      typeof prevSelectedCourse !== 'undefined' &&
      typeof selectedCourse !== 'undefined' &&
      prevSelectedCourse.query_id !== selectedCourse.query_id
    ) {
      this.setState((current) => {
        return {
          ...current,
          course: selectedCourse,
        };
      });
    } else if (prevSelectedCourse !== selectedCourse) {
      this.setState((current) => {
        return {
          ...current,
          course: selectedCourse,
        };
      });
    }

    if (
      typeof prevSelectedStaff !== 'undefined' &&
      typeof selectedStaff !== 'undefined' &&
      prevSelectedStaff.query_id !== selectedStaff.query_id
    ) {
      this.setState((current) => {
        return {
          ...current,
          staff: selectedStaff,
        };
      });
    } else if (prevSelectedStaff !== selectedStaff) {
      this.setState((current) => {
        return {
          ...current,
          staff: selectedStaff,
        };
      });
    }

    const prevSelected = selectedBy(prevSettings, prevProps.renew);
    const selected = selectedBy(settings, this.props.renew);
    if (prevSelected !== selected) {
      this.setState((current) => {
        return {
          ...current,
          selected,
        };
      });
    }
  }

  private handleClickSwitch(selected: IBooks.Switch) {
    this.setState({ ...this.state, selected });
  }

  private async handleClickCourse(course: IBooks.Course) {
    await this.setState({ ...this.state, course });
  }

  private async handleClickStaff(staff: IBooks.Staff) {
    await this.setState({ ...this.state, staff });
  }

  private handleClickOption(options: IBooks.Option[]) {
    this.setState({ ...this.state, selecting: { options } });
  }

  private handleClearTarget(course: boolean, options: boolean, staff: boolean) {
    this.setState({
      ...this.state,
      course: course ? undefined : this.state.course,
      options: options ? undefined : this.state.options,
      selecting: {},
      staff: staff ? undefined : this.state.staff,
    });
  }

  private async handleFixSelection() {
    await this.setState({
      ...this.state,
      options: this.state.selecting.options || [],
    });
  }

  private async handleSubmit() {
    const {
      modelType,
      onSubmit,
      onRestart,
      inputs: { merchantId, reservationId },
      withExpressFlow,
    } = this.props;
    const { selected, course, options, staff } = this.state;
    try {
      await this.props.actions.handleSubmit(
        modelType,
        merchantId!,
        this.props.resourcePublicId,
        course!,
        options || [],
        staff!,
        selected,
        withExpressFlow,
        reservationId,
      );
      onSubmit();
    } catch (e) {
      if (e instanceof RestartReservationError) {
        onRestart();
      }
    }
  }

  public render() {
    const { settings, postable, intl } = this.props;
    const { selected, course, options, staff, selecting } = this.state;

    return (
      <div>
        {showableSwitch(selected, course, staff) && (
          <InputBookingTargetSwitch
            intl={intl}
            selected={selected}
            onClick={this.handleClickSwitch}
          />
        )}
        {selected === IBooks.Switch.Course && (
          <div>
            <CurrentCourseOptions
              course={course}
              options={options}
              onChangeCourse={() => this.handleClearTarget(true, true, true)}
              onChangeOptions={() => this.handleClearTarget(false, true, true)}
            />
            {!course ? (
              // [course] -> ...
              <InputBookingTargetCourseSelector
                courses={settings.courses}
                postable={postable}
                onClick={this.handleClickCourse}
              />
            ) : course.options && course.options.length !== 0 && !options ? (
              // course -> [option] -> staff
              <div>
                <LabeledContainer
                  label={intl.formatMessage({
                    id: 'books.formsLabel.addOptions',
                  })}
                  contentsPadding={false}
                  paddingTop={true}
                >
                  <InputBookingTargetOptionSelector
                    options={course.options}
                    checkedOptions={selecting.options || []}
                    onClick={this.handleClickOption}
                  />
                </LabeledContainer>
                <div className="px-[18px] py-5">
                  <RSButton
                    variant="basic"
                    isBlock
                    value={intl.formatMessage({
                      id: 'books.submitLabel.selectStaff',
                    })}
                    onClick={this.handleFixSelection}
                  >
                    {intl.formatMessage({
                      id: 'books.submitLabel.selectStaff',
                    })}
                  </RSButton>
                </div>
              </div>
            ) : (
              // course -> [staff]
              // course -> option -> [staff]
              <LabeledContainer
                label={intl.formatMessage({
                  id: 'books.formsLabel.selectStaff',
                })}
                contentsPadding={false}
                paddingTop={true}
                noBorderBottom={true}
              >
                <InputBookingTargetStaffSelector
                  random={course.acceptRandomAssign!}
                  staffs={course.staffIds!.map((id) => settings.staffs![id])}
                  postable={postable}
                  onClick={async (_staff: IBooks.Staff) => {
                    await this.handleClickStaff(_staff);
                    await this.handleSubmit();
                  }}
                />
              </LabeledContainer>
            )}
          </div>
        )}
        {selected === IBooks.Switch.Staff && (
          <div>
            <CurrentStaffCourse
              staff={staff as IBooks.RealStaff}
              course={course}
              onChangeStaff={() => this.handleClearTarget(true, true, true)}
              onChangeCourse={() => this.handleClearTarget(true, true, false)}
            />
            {!staff ? (
              // [staff] -> ...
              <InputBookingTargetStaffSelector
                random={false}
                staffs={settings.staffList!}
                postable={postable}
                onClick={this.handleClickStaff}
              />
            ) : !course ? (
              // staff -> [course] -> option
              <LabeledContainer
                label={intl.formatMessage({
                  id: 'books.formsLabel.selectMenu',
                })}
                contentsPadding={false}
                paddingTop={true}
                noBorderBottom={true}
              >
                <InputBookingTargetCourseSelector
                  courses={settings.courses!.filter(
                    (c) =>
                      c.staffIds!.indexOf((staff as IBooks.RealStaff).id) >= 0,
                  )}
                  postable={postable}
                  onClick={async (c: IBooks.Course) => {
                    await this.handleClickCourse(c);
                    if (c.options && c.options.length !== 0) {
                      return;
                    }
                    await this.handleFixSelection();
                    await this.handleSubmit();
                  }}
                />
              </LabeledContainer>
            ) : (
              // staff -> course -> [option]
              <div>
                <LabeledContainer
                  label={intl.formatMessage({
                    id: 'books.formsLabel.addOptions',
                  })}
                  contentsPadding={false}
                  paddingTop={true}
                >
                  <InputBookingTargetOptionSelector
                    options={course.options}
                    checkedOptions={selecting.options || []}
                    onClick={this.handleClickOption}
                  />
                </LabeledContainer>
                <div className="px-[18px] py-5">
                  <RSButton
                    variant="basic"
                    isBlock
                    isLoading={!postable}
                    disabled={!postable}
                    type="button"
                    value={intl.formatMessage({
                      id: 'books.submitLabel.selectDateTime',
                    })}
                    onClick={async () => {
                      await this.handleFixSelection();
                      await this.handleSubmit();
                    }}
                  >
                    {intl.formatMessage({
                      id: 'books.submitLabel.selectDateTime',
                    })}
                  </RSButton>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

const mapStateProps = ({
  merchantsResourcesBooksInputs,
}: {
  merchantsResourcesBooksInputs: IInputs.State;
}): StateProps => ({
  inputs: merchantsResourcesBooksInputs,
});

const mapDispatchToProps = (dispatch: I.Dispatch & B.Dispatch) => ({
  actions: {
    handleSubmit: (
      modelType: IBooks.ModelType,
      merchantId: string,
      resourceId: string,
      course: IBooks.Course,
      options: IBooks.Option[],
      staff: IBooks.Staff,
      selected: IBooks.Switch,
      withExpressFlow: boolean,
      reservationId: number,
    ) =>
      inputBookingTargets(
        modelType,
        merchantId,
        resourceId,
        {
          course,
          options,
          staff,
          selected,
        },
        withExpressFlow,
        reservationId,
      )(dispatch),
  },
});

export default connect(
  mapStateProps,
  mapDispatchToProps,
)(injectIntl(InputBookingTarget));
